import { UiState } from '../state/UiState';
import { t } from '../i18n/i18n';
import { SUPPORTED_LOCALES, SupportedLocale } from '../i18n/types';

export interface HeaderBarCallbacks {
  onFileSelected: (file: File) => void;
  onOpenUrlModal?: () => void;
  onOpenPresetManager: () => void;
  onOpenExportModal: () => void;
  onUpgradeRequested: (featureName: string) => void;
  onToggleControlPanel: () => void;
}

export class HeaderBar {
  private element: HTMLElement;
  private uiState: UiState;
  private callbacks: HeaderBarCallbacks;
  private fileInput: HTMLInputElement;

  private logoTitleEl: HTMLElement | null = null;
  private logoSubtitleEl: HTMLElement | null = null;
  private openFileBtn: HTMLButtonElement | null = null;
  private urlBtn: HTMLButtonElement | null = null;
  private presetBtn: HTMLButtonElement | null = null;
  private presetNameEl: HTMLElement | null = null;
  private exportBtn: HTMLButtonElement | null = null;
  private proBtn: HTMLButtonElement | null = null;
  private togglePanelBtn: HTMLButtonElement | null = null;
  private langSelectEl: HTMLSelectElement | null = null;

  constructor(uiState: UiState, callbacks: HeaderBarCallbacks) {
    this.uiState = uiState;
    this.callbacks = callbacks;
    this.element = document.createElement('header');
    this.element.className = 'app-header';

    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.accept = 'image/*';
    this.fileInput.style.display = 'none';
    this.fileInput.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files[0]) {
        this.callbacks.onFileSelected(target.files[0]);
        target.value = '';
      }
    });

    this.render();

    this.uiState.subscribe((state) => {
      if (this.presetNameEl) {
        this.presetNameEl.textContent = state.currentPresetName;
      }
      this.updateTexts();
    });
  }

  getElement(): HTMLElement {
    return this.element;
  }

  private render(): void {
    const texts = t();
    const currentLocale = this.uiState.getSnapshot().locale;

    const langOptions = SUPPORTED_LOCALES.map(
      (loc) => `<option value="${loc.code}" ${loc.code === currentLocale ? 'selected' : ''}>${loc.flag} ${loc.name}</option>`
    ).join('');

    this.element.innerHTML = `
      <div class="logo-container">
        <div class="logo-icon">🍮</div>
        <div class="logo-text">
          <h1 id="headerLogoTitle">${texts.app.title}</h1>
          <p id="headerLogoSubtitle">${texts.app.subtitle}</p>
        </div>
      </div>

      <div class="header-actions">
        <!-- 画像ファイル読み込み -->
        <button class="btn btn-secondary" id="btnOpenFile" title="${texts.header.openFile}">
          ${texts.header.openFile}
        </button>

        <!-- Web画像URL読み込み (有料版限定) -->
        <button class="btn btn-secondary" id="btnOpenUrl" title="${texts.header.openUrl}">
          ${texts.header.openUrl}
        </button>

        <!-- プリセットクイックセレクター -->
        <button class="btn btn-secondary" id="btnPresetSelect" title="${texts.header.presets}">
          🔖 <span id="currentPresetName">${this.uiState.getSnapshot().currentPresetName}</span> ▼
        </button>

        <!-- エクスポートボタン (無料版でも表示、クリック時にPro版案内) -->
        <button class="btn btn-primary ${this.uiState.isExportAllowed() ? '' : 'btn-disabled'}" id="btnHeaderExport" title="${this.uiState.isExportAllowed() ? texts.header.export : texts.header.exportLocked}">
          ${this.uiState.isExportAllowed() ? texts.header.export : texts.header.exportLocked}
        </button>

        <!-- Pro版アップグレード / ステータスボタン -->
        <button class="btn btn-pro ${this.uiState.isUrlImportAllowed() ? 'pro-active' : ''}" id="btnHeaderPro" title="${texts.header.getProBtn}">
          ${this.uiState.isUrlImportAllowed() ? texts.header.proBadge : texts.header.getProBtn}
        </button>

        <!-- 言語セレクター -->
        <select class="lang-select" id="headerLangSelect" title="${texts.header.langSelect}" aria-label="${texts.header.langSelect}">
          ${langOptions}
        </select>

        <!-- パネル開閉トグル (PC) -->
        <button class="btn btn-secondary btn-icon" id="btnTogglePanel" title="${texts.header.togglePanel}">
          ⚙️
        </button>
      </div>
    `;

    this.element.appendChild(this.fileInput);

    this.logoTitleEl = this.element.querySelector('#headerLogoTitle');
    this.logoSubtitleEl = this.element.querySelector('#headerLogoSubtitle');
    this.openFileBtn = this.element.querySelector('#btnOpenFile');
    this.urlBtn = this.element.querySelector('#btnOpenUrl');
    this.presetBtn = this.element.querySelector('#btnPresetSelect');
    this.presetNameEl = this.element.querySelector('#currentPresetName');
    this.exportBtn = this.element.querySelector('#btnHeaderExport');
    this.proBtn = this.element.querySelector('#btnHeaderPro');
    this.togglePanelBtn = this.element.querySelector('#btnTogglePanel');
    this.langSelectEl = this.element.querySelector('#headerLangSelect');

    this.proBtn?.addEventListener('click', () => {
      this.callbacks.onUpgradeRequested('');
    });

    // イベントバインド
    this.openFileBtn?.addEventListener('click', () => {
      this.fileInput.click();
    });

    this.urlBtn?.addEventListener('click', () => {
      if (this.uiState.isUrlImportAllowed()) {
        this.callbacks.onOpenUrlModal?.();
      } else {
        const curTexts = t();
        this.callbacks.onUpgradeRequested(curTexts.upgrade.perkUrl);
      }
    });

    this.presetBtn?.addEventListener('click', () => {
      this.callbacks.onOpenPresetManager();
    });

    this.exportBtn?.addEventListener('click', () => {
      if (this.uiState.isExportAllowed()) {
        this.callbacks.onOpenExportModal();
      } else {
        const curTexts = t();
        this.callbacks.onUpgradeRequested(curTexts.upgrade.perkExport);
      }
    });

    this.togglePanelBtn?.addEventListener('click', () => {
      this.callbacks.onToggleControlPanel();
    });

    this.langSelectEl?.addEventListener('change', () => {
      if (this.langSelectEl) {
        const newLocale = this.langSelectEl.value as SupportedLocale;
        this.uiState.setLocale(newLocale);
      }
    });

    this.updateTexts();
  }

  private updateTexts(): void {
    const texts = t();

    if (this.logoTitleEl) this.logoTitleEl.textContent = texts.app.title;
    if (this.logoSubtitleEl) this.logoSubtitleEl.textContent = texts.app.subtitle;
    if (this.openFileBtn) {
      this.openFileBtn.textContent = texts.header.openFile;
      this.openFileBtn.title = texts.header.openFile;
    }
    if (this.presetBtn) {
      this.presetBtn.title = texts.header.presets;
    }
    if (this.togglePanelBtn) {
      this.togglePanelBtn.title = texts.header.togglePanel;
    }
    if (this.langSelectEl) {
      this.langSelectEl.title = texts.header.langSelect;
      this.langSelectEl.value = this.uiState.getSnapshot().locale;
    }

    if (this.proBtn) {
      const isPro = this.uiState.isUrlImportAllowed();
      this.proBtn.textContent = isPro ? texts.header.proBadge : texts.header.getProBtn;
      this.proBtn.title = isPro ? texts.header.proBadge : texts.header.getProBtn;
      this.proBtn.classList.toggle('pro-active', isPro);
    }

    this.updateExportButtonState();
    this.updateUrlButtonState();
  }

  private updateExportButtonState(): void {
    if (!this.exportBtn) return;
    const texts = t();
    const isAllowed = this.uiState.isExportAllowed();
    this.exportBtn.style.display = 'inline-flex';
    if (isAllowed) {
      this.exportBtn.textContent = texts.header.export;
      this.exportBtn.classList.remove('btn-disabled');
      this.exportBtn.title = texts.header.export;
    } else {
      this.exportBtn.textContent = texts.header.exportLocked;
      this.exportBtn.classList.add('btn-disabled');
      this.exportBtn.title = texts.header.exportLocked;
    }
  }

  private updateUrlButtonState(): void {
    if (!this.urlBtn) return;
    const texts = t();
    const isAllowed = this.uiState.isUrlImportAllowed();
    if (isAllowed) {
      this.urlBtn.textContent = texts.header.openUrl;
      this.urlBtn.classList.remove('btn-disabled');
      this.urlBtn.title = texts.header.openUrl;
    } else {
      this.urlBtn.textContent = texts.header.openUrlLocked;
      this.urlBtn.classList.add('btn-disabled');
      this.urlBtn.title = texts.header.openUrlLocked;
    }
  }
}
